import { useState, useEffect } from 'react'
import { Item } from '../../../../../utils/types/generals';
import { InputModel } from '../../../../../utils/types';
import { Roles } from '../../../../../utils/enums';
import { removeAllReference } from '../../../../../utils/methods/removedReference';
import { department } from '../../../../../locale/es/department';

const useInput = ({ items }: { items?: { [x: string]: Item[] } }) => {
  const [input, setInput] = useState<{ [x: string]: InputModel }>({
    search: {
      label: 'schedule.filter.inputs.search',
      translate: true,
      name: 'search',
      rest: {
        type: 'alphaNumeric',
      },
      value: null,
    },
    createYear: {
      label: 'general.filterDate.year',
      translate: true,
      name: 'createYear',
      type: 'filter-select',
      items: [],
      value: null,
      size: {
        xl: 6,
        lg: 6,
        md: 6,
        sm: 6,
        xs: 6,
      },
    },
    month: {
      label: 'general.filterDate.month',
      translate: true,
      name: 'month',
      type: 'filter-select',
      items: [],
      value: null,
      size: {
        xl: 6,
        lg: 6,
        md: 6,
        sm: 6,
        xs: 6,
      },
    },
    from: {
      label: 'general.tags.from',
      translate: true,
      name: 'from',
      type: 'date',
      value: null,
      rest: {
        max: 'to',
      },
      size: {
        xl: 6,
        lg: 6,
        md: 6,
        sm: 6,
        xs: 6,
      },
    },
    to: {
      label: 'general.tags.to',
      translate: true,
      name: 'to',
      type: 'date',
      value: null,
      rest: {
        min: 'from',
      },
      size: {
        xl: 6,
        lg: 6,
        md: 6,
        sm: 6,
        xs: 6,
      },
    },
    open_scheduled: {
      label: 'general.tags.open_scheduled',
      translate: true,
      name: 'open_scheduled',
      value: false,
      type: 'switch',
      size: {
        xl: 6,
        lg: 6,
        md: 6,
        sm: 6,
        xs: 6,
      },
    },
    filterByCurrentWeek: {
      label: 'general.tags.filterByCurrentWeek',
      translate: true,
      name: 'filterByCurrentWeek',
      value: false,
      type: 'switch',
      size: {
        xl: 6,
        lg: 6,
        md: 6,
        sm: 6,
        xs: 6,
      },
    },
    programmed: {
      label: 'general.tags.programmed',
      translate: true,
      name: 'programmed',
      value: false,
      type: 'switch',
      size: {
        xl: 12,
        lg: 12,
        md: 12,
        sm: 12,
        xs: 12,
      },
    },
    status: {
      label: 'general.tags.status',
      translate: true,
      name: 'status',
      type: 'filter-select',
      items: [],
      value: null,
      size: {
        xl: 12,
        lg: 12,
        md: 12,
        sm: 12,
      },
    },
    areas: {
      label: 'general.tags.area',
      translate: true,
      name: 'areas',
      type: 'filter-select',
      items: [],
      value: null,
      size: {
        xl: 12,
        lg: 12,
        md: 12,
        sm: 12,
      },
    },
    departments: {
      label: 'general.tags.department',
      translate: true,
      name: 'departments',
      type: 'filter-select',
      items: [],
      value: null,
      size: {
        xl: 12,
        lg: 12,
        md: 12,
        sm: 12,
      },
    },
    questionnaireId: {
      label: 'general.tags.questionnaire',
      translate: true,
      name: 'questionnaireId',
      type: 'filter-select',
      items: [],
      value: [],
    },
    percentage: {
      label: 'general.tags.percentage',
      translate: true,
      name: 'percentage',
      type: 'percentage',
      value: null,
    },
    userTypeId: {
      label: 'general.tags.userType',
      translate: true,
      name: 'userTypeId',
      type: 'filter-select',
      items: [],
      value: [],
      notAllowedRoles: [Roles.AUDIT],
    },
    userId: {
      label: 'general.tags.user',
      translate: true,
      name: 'userId',
      type: 'filter-select',
      items: [],
      value: [],
      notAllowedRoles: [Roles.AUDIT],
    },
    roles: {
      label: 'general.tags.role',
      translate: true,
      name: 'roles',
      type: 'filter-select',
      items: [],
      value: null,
      notAllowedRoles: [Roles.STORE, Roles.AUDIT],
    },
    divisions: {
      label: 'general.tags.division',
      translate: true,
      name: 'divisions',
      type: 'filter-select',
      items: [],
      value: null,
      notAllowedRoles: [Roles.AUDIT],
    },
    key_codes_origin_st: {
      label: 'useScheduled.referenceOrigynStore',
      translate: true,
      name: 'key_codes_origin_st',
      type: 'filter-select',
      items: [],
      value: [],
    },
  })

  const disabledInputs = (data: { inputs: string[]; value: boolean }) => {
    let copyInput: {
      [x: string]: InputModel
    } = { ...input }

    data?.inputs?.forEach((item) => {
      copyInput[item] = {
        ...copyInput[item],
        rest: {
          ...(copyInput[item]?.rest ?? {}),
          disabled: data.value,
        },
      }
    })

    setInput((prev) => ({
      ...prev,
      ...copyInput,
    }))
  }
  useEffect(() => {
    let body = {}
    Object.entries(input).forEach(([key, itemInputs]) => {
      body = { ...body, [key]: { ...itemInputs, items: items?.[key] } }
    })

    setInput(prev => ({
      ...prev,
      createYear: { ...prev.createYear, items: items?.createYear },
      month: { ...prev.month, items: items?.month },
      status: { ...prev.status, items: items?.status },
      areas: { ...prev.areas, items: items?.areas },
      questionnaireId: { ...prev.questionnaireId, items: items?.questionnaireId },
      userTypeId: { ...prev.userTypeId, items: items?.userTypeId },
      userId: { ...prev.userId, items: items?.userId },
      roles: { ...prev.roles, items: items?.roles },
      divisions: { ...prev.divisions, items: items?.divisions },
      departments: { ...prev.departments, items: items?.departments },
      key_codes_origin_st: { ...prev.key_codes_origin_st, items: items?.key_codes_origin_st },
    }))
    // eslint-disable-next-line
  }, [items?.areas, items?.departments, items?.createYear, items?.divisions, items?.month, items?.questionnaireId, items?.roles, items?.status, items?.userId, items?.userTypeId])

  return { input, disabledInputs }
}

export { useInput }
